import { META_PIXEL_ID } from "@/constants";

/**
 * Píxel de Meta. Cumple dos funciones y la segunda es la que de verdad importa:
 *
 * 1. Manda los eventos de embudo del navegador (`Lead`, `AddToCart`, …).
 * 2. **Deja las cookies `_fbp` y `_fbc`**, que son lo único que permite atribuir
 *    una compra al anuncio que la originó días después. Sin el píxel corriendo en
 *    la app, esas cookies no existen en este dominio y la venta llega anónima.
 *
 * Queda inerte sin `BUN_PUBLIC_META_PIXEL_ID`, igual que PostHog sin su token.
 */

/**
 * Qué evento nuestro se convierte en cuál de Meta. Lo que no está acá no viaja:
 * la lista se mantiene corta a propósito, porque cada evento extra le mete ruido
 * al modelo en vez de información.
 *
 * Una clave puede ser `evento` o `evento:kind`, y la segunda gana. Sirve para que
 * un mismo evento nuestro viaje a Meta solo con ciertos `kind`, sin tener que
 * partirlo en dos y romper el histórico de PostHog.
 *
 * `signup_clicked` NO está y no debe estar: mandarlo como `Lead` es justamente el
 * error que este trabajo corrige — entrenaba a Meta para buscar gente que aprieta
 * botones.
 */
const META_EVENTS: Record<string, { name: string; standard: boolean }> = {
  onboarding_finished: { name: "Lead", standard: true },
  contracting_unlocked: { name: "ViewContent", standard: true },
  // El ÚNICO upsell que es intención de compra: se quedó sin vistas y pulsó subir
  // de plan. Los otros `upsell_cta_clicked` quedan fuera a propósito — el tope de
  // líneas es un límite que no siempre se resuelve pagando, y el del chat con IA
  // es "hablar con ventas" de algo que ni está en la tabla de precios.
  "upsell_cta_clicked:no_views": { name: "AddToCart", standard: true },
  checkout_opened: { name: "InitiateCheckout", standard: true },
  checkout_submitted: { name: "AddPaymentInfo", standard: true },
  // No son estándar de Meta: describen nuestro canal de venta por WhatsApp, que
  // en el embudo pesa tanto como el checkout. Los tres caen en el mismo nombre a
  // propósito — fragmentarlo partiría el público de retargeting en tres; de qué
  // botón salió cada uno lo dice su `source`.
  sales_whatsapp_clicked: { name: "SalesContact", standard: false },
  plan_contact_clicked: { name: "SalesContact", standard: false },
  corporate_contact_clicked: { name: "SalesContact", standard: false },
};

type Fbq = ((...args: unknown[]) => void) & {
  queue?: unknown[];
  callMethod?: (...args: unknown[]) => void;
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export function initMetaPixel(): void {
  if (!META_PIXEL_ID || window.fbq) return;
  // Envuelve el arranque entero: este provider envuelve al router, así que una
  // excepción acá dejaría la app en blanco.
  try {
    // El snippet oficial de Meta, tal cual. `fbevents.js` NO reemplaza esta
    // función: le cuelga `callMethod` y drena la cola una sola vez al cargar, así
    // que sin la rama de despacho todo lo posterior se encola para siempre.
    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
      else fbq.queue!.push(args);
    };
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    // Los lee `fbevents.js` (y el Pixel Helper) para saber que hay snippet puesto.
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  } catch (err) {
    console.warn("[meta-pixel] No se pudo inicializar:", err);
  }
}

/**
 * Traduce un evento de producto a su equivalente en Meta, si lo tiene. Lo llama
 * `capture()`: no se invoca a mano desde las pantallas, para que exista un solo
 * sitio donde se decide qué se le reporta a la publicidad.
 */
export function trackMetaEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  const mapped =
    META_EVENTS[`${event}:${properties?.kind}`] ?? META_EVENTS[event];
  if (!mapped || !window.fbq) return;

  // `capture()` corre en medio de flujos reales (incluido el manejo de errores de
  // `fetchToBackend`), así que un fallo del script de Meta no puede tumbar una
  // pantalla.
  try {
    // Sin `eventID`: hoy ningún evento sale por navegador y por CAPI a la vez, y
    // el `event_id` solo sirve para emparejar los dos lados. Cuando exista un
    // gemelo, tiene que llegar el MISMO id que manda el servidor
    // (`registration_<userId>`), nunca uno aleatorio: eso contaría doble.
    window.fbq(
      mapped.standard ? "track" : "trackCustom",
      mapped.name,
      properties,
    );
  } catch (err) {
    console.warn("[meta-pixel] No se pudo enviar el evento:", event, err);
  }
}
